// client/src/components/ClientPhotoViewer.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; 
import '../CSS/PhotoViewer.css'; 
import { firestore, storage } from '../../firebase'; 
import { doc, collection, getDocs, getDoc, onSnapshot, query, limit as fsLimit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth'; 
import { ref, deleteObject, updateMetadata, getMetadata } from 'firebase/storage';
import { FaDownload, FaTrash, FaArrowLeft } from 'react-icons/fa';
import { useDataContext } from '../../DataContext';
import { sanitize } from '../../utils/sanitize';
import { isShared, markShared, unshare } from '../../utils/shareStore';

// ---------- Caches & TTL (hybride) ----------
const COMPANY_UID_CACHE_KEY = 'acceptedCompanyUID';
const COMPANY_UID_LASTSYNC_KEY = 'acceptedCompanyUID_lastSync';
const INVITATIONS_CACHE_KEY = 'client_invitations_cache';
const INVITATIONS_LASTSYNC_KEY = 'client_invitations_lastSync';

const COMPANY_UID_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const INVITATIONS_TTL_MS = 15 * 60 * 1000;      // 15 min
// Firestore rules: list queries require limit <= 50
const INV_LIMIT_SCAN = 50;
const MEMO_TTL_MS = 24 * 60 * 60 * 1000;

// Mémo in-memory (par onglet)
const acceptedCompanyMemo = new Map();
const invitationsMemo = new Map();

const needRefresh = (key, ttl) => {
  const raw = localStorage.getItem(key);
  const last = Number(raw || 0);
  return !Number.isFinite(last) || Date.now() - last > ttl;
};

const loadCachedCompanyUid = () => {
  try {
    const raw = localStorage.getItem(COMPANY_UID_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {}
  return null;
};

const saveCachedCompanyUid = (uidValue) => {
  try {
    localStorage.setItem(COMPANY_UID_CACHE_KEY, JSON.stringify(uidValue));
    localStorage.setItem(COMPANY_UID_LASTSYNC_KEY, String(Date.now()));
  } catch {}
};

const loadCachedInvitations = () => {
  try {
    const raw = localStorage.getItem(INVITATIONS_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {}
  return [];
};

const saveCachedInvitations = (list) => {
  try {
    localStorage.setItem(INVITATIONS_CACHE_KEY, JSON.stringify(list || []));
    localStorage.setItem(INVITATIONS_LASTSYNC_KEY, String(Date.now()));
  } catch {}
};

const shallowEqualBy = (a, b, key) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i][key] !== b[i][key]) return false;
  }
  return true;
};

// ---------- Gestion des titres par défaut ----------
const DEFAULT_TITLES = new Set([
  'AUCUN TITRE',
  'SANS TITRE',
  'UNTITLED',
  'NO TITLE',
  'N/A',
]);

const isDefaultTitle = (t) => {
  if (!t) return true;
  const norm = String(t).trim();
  if (norm === '') return true;
  return DEFAULT_TITLES.has(norm.toUpperCase());
};

const normalizeTitle = (t) => sanitize(String(t || '').trim());

const extractInitialTitle = (photo) => {
  if (!photo) return '';
  const fromProp = photo.title;
  const fromMeta = photo?.metadata?.customMetadata?.title || photo?.metadata?.title;
  const candidate = normalizeTitle(fromProp || fromMeta || '');
  return isDefaultTitle(candidate) ? '' : candidate;
};

const ClientPhotoViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { photo } = location.state || {};

  const [isCompanyLinked, setIsCompanyLinked] = useState(null);
  const [photos, setPhotos] = useState([]);
  // eslint-disable-next-line
  const [companyUID, setCompanyUID] = useState(null);
  const [sharedCompany, setSharedCompany] = useState(null);
  const [invitationStatus, setInvitationStatus] = useState(null);
  // eslint-disable-next-line
  const [user, setUser] = useState(null);
  // eslint-disable-next-line
  const [data, setData] = useState(null);
  const { fetchData /*, dataCache*/ } = useDataContext();
  // eslint-disable-next-line
  const [invitations, setInvitations] = useState([]);

  const [currentPhoto, setCurrentPhoto] = useState(photo || null);

  // Titre affiché (lecture seule hors édition)
  const [photoTitle, setPhotoTitle] = useState('');
  // Brouillon du champ input pendant l'édition
  const [draftTitle, setDraftTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Orientation (pour limiter la taille des images verticales)
  const [isPortrait, setIsPortrait] = useState(false);

  const deleteBusyRef = useRef(false);
  const toggleBusyRef = useRef(false);
  const saveTitleBusyRef = useRef(false);

    // ------- Helpers mémoïsés -------
  const getAcceptedCompanyUID = useCallback(
    async (email) => {
      if (!email) return null;

      // 0) Mémo en mémoire : on ne réutilise que si on a une vraie valeur
      const m = acceptedCompanyMemo.get(email);
      if (m && m.expiresAt > Date.now() && m.uid) {
        return m.uid;
      }

      // 1) Cache localStorage : pareil, uniquement si non null
      if (!needRefresh(COMPANY_UID_LASTSYNC_KEY, COMPANY_UID_TTL_MS)) {
        const cached = loadCachedCompanyUid();
        if (cached) {
          acceptedCompanyMemo.set(email, {
            uid: cached,
            expiresAt: Date.now() + MEMO_TTL_MS,
          });
          return cached;
        }
      }

      // 2) Lecture du doc "users" (champ acceptedCompanyUID)
      try {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;
        if (uid) {
          const uref = doc(firestore, 'users', uid);
          const usnap = await getDoc(uref);
          const udata = usnap.exists() ? usnap.data() : {};
          const val = udata?.acceptedCompanyUID || null;

          if (val) {
            saveCachedCompanyUid(val);
            acceptedCompanyMemo.set(email, {
              uid: val,
              expiresAt: Date.now() + MEMO_TTL_MS,
            });
            return val;
          }
        }
      } catch {
        // on ignore, on tentera la suite
      }

      // 3) Fallback : recherche dans "entreprises" (invitations.status === 'accepted')
      try {
        const entreprisesRef = collection(firestore, 'entreprises');
        const querySnapshot = await getDocs(query(entreprisesRef, fsLimit(INV_LIMIT_SCAN)));
        let acceptedCompanyUid = null;

        for (const docSnap of querySnapshot.docs) {
          const entrepriseData = docSnap.data() || {};
          const invs = Array.isArray(entrepriseData.invitations)
            ? entrepriseData.invitations
            : [];
          for (const inv of invs) {
            if (inv.employeeEmail === email && inv.status === 'accepted') {
              acceptedCompanyUid = entrepriseData.userId || null;
              break;
            }
          }
          if (acceptedCompanyUid) break;
        }

        if (acceptedCompanyUid) {
          saveCachedCompanyUid(acceptedCompanyUid);
          acceptedCompanyMemo.set(email, {
            uid: acceptedCompanyUid,
            expiresAt: Date.now() + MEMO_TTL_MS,
          });
          return acceptedCompanyUid;
        }

        return null;
      } catch {
        const cached = loadCachedCompanyUid();
        if (cached) {
          acceptedCompanyMemo.set(email, {
            uid: cached,
            expiresAt: Date.now() + MEMO_TTL_MS,
          });
          return cached;
        }
        return null;
      }
    },
    []
  );


  const getPendingInvitations = useCallback(
    async (email) => {
      if (!email) return [];

      const m = invitationsMemo.get(email);
      if (m && m.expiresAt > Date.now()) return m.list || [];

      if (!needRefresh(INVITATIONS_LASTSYNC_KEY, INVITATIONS_TTL_MS)) {
        const cached = loadCachedInvitations();
        invitationsMemo.set(email, { list: cached, expiresAt: Date.now() + INVITATIONS_TTL_MS });
        return cached;
      }

      try {
        const entrepriseRef = collection(firestore, 'entreprises');
        const querySnapshot = await getDocs(entrepriseRef);
        const fetchedInvitations = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const invs = Array.isArray(data.invitations) ? data.invitations : [];
          invs.forEach((inv) => {
            if (inv.employeeEmail === email && inv.status === 'pending') {
              fetchedInvitations.push({ id: docSnap.id, ...inv });
            }
          });
        });
        saveCachedInvitations(fetchedInvitations);
        invitationsMemo.set(email, { list: fetchedInvitations, expiresAt: Date.now() + INVITATIONS_TTL_MS });
        return fetchedInvitations;
      } catch {
        const cached = loadCachedInvitations();
        invitationsMemo.set(email, { list: cached, expiresAt: Date.now() + INVITATIONS_TTL_MS });
        return cached;
      }
    },
    []
  );

  // ---------- Initialisation du titre ----------
  useEffect(() => {
    if (!photo) return;
    setPhotoTitle(extractInitialTitle(photo));
  }, [photo]);

    // ---------- Hydratation currentPhoto ----------
  useEffect(() => {
    if (!photo) return;

    // On essaie de récupérer une version plus à jour depuis localStorage.userPhotos
    let mergedPhoto = photo;
    try {
      const storedPhotos = JSON.parse(localStorage.getItem('userPhotos')) || [];
      const match = storedPhotos.find((p) => p.name === photo.name);
      if (match) {
        mergedPhoto = {
          ...mergedPhoto,
          ...match,
          metadata: {
            ...(mergedPhoto.metadata || {}),
            ...(match.metadata || {}),
            customMetadata: {
              ...((mergedPhoto.metadata && mergedPhoto.metadata.customMetadata) || {}),
              ...((match.metadata && match.metadata.customMetadata) || {}),
            },
          },
        };
      }
    } catch {
      // si localStorage est foireux, on continue juste avec "photo"
    }

    const baseMetadata = mergedPhoto.metadata || {};
    const baseCustom = baseMetadata.customMetadata || {};
    const fallbackCategory = mergedPhoto.category || baseCustom.category || 'perso';
    const resolvedCategory = sharedCompany || fallbackCategory || 'perso';

    const derivedIsPro =
      sharedCompany != null
        ? true
        : typeof mergedPhoto.isPro === 'boolean'
          ? mergedPhoto.isPro
          : false;

    setCurrentPhoto((prev) => {
      const next = {
        ...mergedPhoto,
        category: resolvedCategory,
        metadata: {
          ...baseMetadata,
          customMetadata: {
            ...baseCustom,
            category: resolvedCategory,
          },
        },
        isPro: derivedIsPro,
      };

      if (prev && prev.name === next.name) {
        return { ...next, title: prev.title ?? next.title };
      }
      return next;
    });
  }, [photo, companyUID, sharedCompany]);

  // ---------- Statut de partage (Firestore, sans metadata Storage) ----------
  useEffect(() => {
    let active = true;
    const fetchShareState = async () => {
      try {
        const auth = getAuth();
        const ownerUid = auth.currentUser?.uid;
        if (!photo || !companyUID || !ownerUid) {
          if (active) setSharedCompany(null);
          return;
        }
        const shared = await isShared({
          companyUid: companyUID,
          ownerUid,
          fileName: photo.name,
        });
        if (active) {
          setSharedCompany(shared ? companyUID : null);
        }
      } catch {
        if (active) setSharedCompany(null);
      }
    };
    fetchShareState();
    return () => {
      active = false;
    };
  }, [photo?.name, companyUID, photo]);


  // ---------- Auth + user ----------
  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        navigate('/');
        return;
      }

      const userId = firebaseUser.uid;
      const userDocRef = doc(firestore, 'users', userId);

      const unsubscribeUser = onSnapshot(userDocRef, async (docSnapshot) => {
        if (!docSnapshot.exists()) {
          console.error("Le document utilisateur n'existe pas pour uid :", userId);
          return;
        }

        const userData = docSnapshot.data() || {};
        setUser({ ...userData, pseudo: sanitize(userData?.pseudo ?? '') });

        if (!firebaseUser.isAnonymous) {
          const email = userData?.email;
          if (email) {
            getPendingInvitations(email).then((list) => {
              setInvitations((prev) => (shallowEqualBy(prev, list, 'id') ? prev : list));
            });

            getAcceptedCompanyUID(email).then((uid) => {
              const hasCompany = Boolean(uid);
              setCompanyUID(uid || null);
              setInvitationStatus(hasCompany ? 'accepted' : (invitations?.length ? 'pending' : null));
              setIsCompanyLinked(hasCompany);
              if (!hasCompany) navigate('/clientdocs');
            });
          } else {
            console.warn("Email non trouvé dans userData, mais l'utilisateur n'est pas anonyme.");
            setIsCompanyLinked(false);
            navigate('/clientdocs');
          }
        } else {
          console.log("Utilisateur anonyme détecté.");
          setIsCompanyLinked(false);
          navigate('/clientdocs');
        }

        try {
          const storedClientInvoices = localStorage.getItem('clientInvoices');
          if (storedClientInvoices) {
            setData(JSON.parse(storedClientInvoices));
          } else {
            fetchData('clientInvoices')
              .then((fetchedData) => {
                setData(fetchedData);
                if (fetchedData && fetchedData.length > 0) {
                  localStorage.setItem('clientInvoices', JSON.stringify(fetchedData));
                }
              })
              .catch((error) => {
                console.error('Erreur lors de la récupération des données clientInvoices :', error);
              });
          }
        } catch (error) {
          console.error('Erreur localStorage clientInvoices :', error);
        }
      });

      return () => unsubscribeUser();
    });

    return () => unsubscribeAuth();
    // eslint-disable-next-line 
  }, [navigate, fetchData, getPendingInvitations, getAcceptedCompanyUID]);

  // ---------- Redirection si non affilié ----------
  useEffect(() => {
    if (isCompanyLinked === false) navigate('/clientdocs');
  }, [isCompanyLinked, navigate]);

  // ---------- Pas de photo ----------
  if (isCompanyLinked === null) return null;
  if (!photo || isCompanyLinked === false) {
    return <div>Aucune photo à afficher.</div>;
  }

  // ---------- Actions UI ----------
  const handleBackClick = () => {
    navigate('/clientinvoices');
  };

  const handleToggleCategory = async (photoToToggle) => {
    if (toggleBusyRef.current) return;
    toggleBusyRef.current = true;

    try {
      const userId = getAuth().currentUser?.uid;
      if (!userId) return;

      const willBePro = !Boolean(photoToToggle?.isPro || sharedCompany);

      if (invitationStatus !== 'accepted' && willBePro) {
        alert("Pas d'entreprise affiliée");
        return;
      }

      let resolvedCompanyUid = companyUID;
      if (!resolvedCompanyUid) {
        const authInner = getAuth();
        const userInner = authInner.currentUser;
        if (userInner) {
          const userDocRef = doc(firestore, 'users', userInner.uid);
          const userDoc = await getDoc(userDocRef);
          const userDataInner = userDoc.data() || {};
          const email = userDataInner?.email;
          resolvedCompanyUid = email ? await getAcceptedCompanyUID(email) : null;
          setCompanyUID(resolvedCompanyUid || null);
        }
      }

      if (willBePro && !resolvedCompanyUid) {
        alert("Affiliation introuvable");
        return;
      }

      // Optimiste local
      setCurrentPhoto((prevPhoto) => {
        const previous = prevPhoto || photoToToggle;
        const prevMetadata = previous?.metadata || {};
        const prevCustom = prevMetadata.customMetadata || {};

        return {
          ...previous,
          category: willBePro ? resolvedCompanyUid : 'perso',
          isPro: willBePro,
          metadata: {
            ...prevMetadata,
            customMetadata: {
              ...prevCustom,
              category: willBePro ? resolvedCompanyUid : 'perso',
            },
          },
        };
      });

      // Partage Firestore (sans metadata Storage)
      if (willBePro) {
        await markShared({
          companyUid: resolvedCompanyUid,
          ownerUid: userId,
          fileName: photoToToggle.name,
          storagePath: `${userId}/photos/${photoToToggle.name}`,
          title: photoToToggle.title || currentPhoto?.title || '',
          takenByPseudo:
            photoToToggle?.metadata?.customMetadata?.TakenByPseudo ||
            photoToToggle?.metadata?.TakenByPseudo ||
            '',
          timestamp: photoToToggle.timestamp || Date.now(),
        });
        setSharedCompany(resolvedCompanyUid);
      } else if (resolvedCompanyUid) {
        await unshare({
          companyUid: resolvedCompanyUid,
          ownerUid: userId,
          fileName: photoToToggle.name,
        });
        setSharedCompany(null);
      }

      // Cache local
      const userPhotos = JSON.parse(localStorage.getItem('userPhotos')) || [];
      let found = false;
      const updatedUserPhotos = userPhotos.map((p) => {
        if (p.name === photoToToggle.name) {
          found = true;
          return {
            ...p,
            category: willBePro ? resolvedCompanyUid : 'perso',
            isPro: willBePro,
          };
        }
        return p;
      });

      if (!found) {
        const fallbackEntry = {
          name: photoToToggle.name,
          url: photoToToggle.url,
          timestamp: photoToToggle.timestamp || Date.now(),
          title: photoToToggle.title || '',
          category: willBePro ? resolvedCompanyUid : 'perso',
          isPro: willBePro,
          metadata: {
            ...(photoToToggle.metadata || {}),
            customMetadata: {
              ...((photoToToggle.metadata && photoToToggle.metadata.customMetadata) || {}),
              category: willBePro ? resolvedCompanyUid : 'perso',
            },
          },
        };
        updatedUserPhotos.push(fallbackEntry);
      }

      localStorage.setItem('userPhotos', JSON.stringify(updatedUserPhotos));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la catégorie :', error);
      alert("Erreur lors de la mise à jour des métadonnées.");
    } finally {
      toggleBusyRef.current = false;
    }
  };

  const handleDownloadPhoto = async (photoToDownload) => {
    try {
      const response = await fetch(photoToDownload.url);
      const blob = await response.blob();

      const metadata = photoToDownload.metadata || {};
      const rawTitle =
        metadata.title ||
        (metadata.customMetadata && metadata.customMetadata.title) ||
        null;
      const rawPseudo =
        metadata.TakenByPseudo ||
        (metadata.customMetadata && metadata.customMetadata.TakenByPseudo) ||
        null;

      const title = rawTitle ? sanitize(rawTitle).toUpperCase().replace(/\s+/g, '_') : null;
      const date = photoToDownload.timestamp
        ? new Date(photoToDownload.timestamp).toLocaleDateString('fr-FR').replace(/\//g, '-')
        : null;
      const employeePseudo = rawPseudo ? sanitize(rawPseudo).toUpperCase().replace(/\s+/g, '_') : null;

      let fileName = 'FACTURE.png';
      if (date && title && employeePseudo) fileName = `${date}-${title}-${employeePseudo}.png`;
      else if (date && title) fileName = `${date}-${title}.png`;
      else if (date && employeePseudo) fileName = `${date}-${employeePseudo}.png`;
      else if (date) fileName = `${date}.png`;
      else if (title) fileName = `${title}.png`;
      else if (employeePseudo) fileName = `${employeePseudo}.png`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement de la photo :', error);
    }
  };

  const handleDeletePhoto = async (photoName, category) => {
    if (deleteBusyRef.current) return;
    deleteBusyRef.current = true;

    try {
      const userId = getAuth().currentUser?.uid;
      if (!userId) return;

      const storagePath = `${userId}/photos/${photoName}`;

      const photoRef = ref(storage, storagePath);
      await deleteObject(photoRef);

      if (companyUID) {
        await unshare({
          companyUid: companyUID,
          ownerUid: userId,
          fileName: photoName,
        }).catch(() => {});
      }

      const updatedPhotos = photos.filter((p) => p.name !== photoName);
      setPhotos(updatedPhotos);

      const storedPhotos = JSON.parse(localStorage.getItem('userPhotos')) || [];
      const updatedStoredPhotos = storedPhotos.filter((p) => p.name !== photoName);
      localStorage.setItem('userPhotos', JSON.stringify(updatedStoredPhotos));

      navigate('/clientinvoices', { replace: true });
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        console.warn("La photo n'existe plus dans Firebase Storage.");
        alert("Cette photo n'existe plus.");
      } else {
        console.error('Erreur lors de la suppression de la photo :', error);
        alert('Erreur lors de la suppression de la photo.');
      }
    } finally {
      deleteBusyRef.current = false;
    }
  };

  const saveTitleToMetadata = async (photoName, title) => {
    if (saveTitleBusyRef.current) return;
    const cleanTitle = normalizeTitle(title);

    // Si le brouillon est identique au titre actuel, ne rien faire
    const currentTitle =
      currentPhoto?.metadata?.customMetadata?.title ||
      currentPhoto?.title ||
      '';

    if (cleanTitle === normalizeTitle(currentTitle)) return;

    saveTitleBusyRef.current = true;

    try {
      const userId = getAuth().currentUser?.uid;
      if (!userId) return;

      const photoRef = ref(storage, `${userId}/photos/${photoName}`);
      const metadata = await getMetadata(photoRef);
      await updateMetadata(photoRef, {
        customMetadata: {
          ...(metadata.customMetadata || {}),
          // On enregistre vide si rien saisi
          title: cleanTitle,
        },
      });

      // Maj du cache local
      const userPhotos = JSON.parse(localStorage.getItem('userPhotos')) || [];
      const updated = userPhotos.map((p) =>
        p.name === photoName
          ? {
              ...p,
              title: cleanTitle,
              metadata: {
                ...(p.metadata || {}),
                customMetadata: {
                  ...((p.metadata && p.metadata.customMetadata) || {}),
                  title: cleanTitle,
                },
              },
            }
          : p
      );
      localStorage.setItem('userPhotos', JSON.stringify(updated));
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du titre dans customMetadata :", error);
    } finally {
      saveTitleBusyRef.current = false;
    }
  };

  const commitTitleAndExit = () => {
    const clean = normalizeTitle(draftTitle);
    saveTitleToMetadata(currentPhoto.name, clean);

    // Met à jour l'état local d'affichage
    const existingMetadata = currentPhoto.metadata || {};
    const existingCustom = existingMetadata.customMetadata || {};
    const updatedPhoto = {
      ...currentPhoto,
      title: clean,
      metadata: {
        ...existingMetadata,
        customMetadata: {
          ...existingCustom,
          title: clean,
        },
      },
    };
    setCurrentPhoto(updatedPhoto);
    setPhotoTitle(clean);
    setIsEditing(false);
  };

  const handleBlur = () => {
    commitTitleAndExit();
  };

  // ---------- Styles d'image dynamiques (ratio conservé) ----------
  const baseImageStyle = {
    objectFit: 'contain',
    borderRadius: 'var(--eb-radius-sm)',
  };

  const landscapeStyle = {
    width: '100%',
    height: 'auto',
    maxWidth: '900px',
    maxHeight: '85vh',
  };

  const portraitStyle = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'min(700px, 90vw)',
    maxHeight: '70vh',
  };

  // ---------- Rendu ----------
  return (
    <div className="photo-viewer-wrapper pvw-root">
      {/* Barre top : gauche = retour, droite = téléchargement */}
      <div className="pvw-bar">
        <button type="button" onClick={handleBackClick} className="back-button pvw-btn ghost">
          <FaArrowLeft className="back-icon" /> <span>Retour</span>
        </button>

        <button
          className="pvw-btn primary icon-only pvw-download-top"
          onClick={() => handleDownloadPhoto(photo)}
          title="Télécharger"
          aria-label="Télécharger"
          type="button"
        >
          <FaDownload className="icon" />
        </button>
      </div>


      {/* Titre */}
      <div className="photo-title-container pvw-title">
        {!isEditing ? (
          <span
            onClick={() => {
              setIsEditing(true);
              // Si c'était un titre par défaut (ex: "SANS TITRE"), on ouvre l'input vide.
              // Sinon, on pré-remplit avec le titre existant.
              setDraftTitle(isDefaultTitle(photoTitle) ? '' : photoTitle);
            }}
            className="photo-title-text pvw-title-text"
            style={{
              color: isDefaultTitle(photoTitle) ? 'var(--eb-subtle)' : 'inherit',
              textTransform: isDefaultTitle(photoTitle) ? 'none' : 'uppercase',
            }}
            title="Cliquer pour modifier"
          >
            {!isDefaultTitle(photoTitle) ? photoTitle.toUpperCase() : 'Ajouter un titre'}
          </span>
        ) : (
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitTitleAndExit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                // Annule l'édition sans sauvegarder
                setIsEditing(false);
                setDraftTitle('');
              }
            }}
            autoFocus
            className="photo-title-input pvw-title-input"
            placeholder="Saisir un titre…"
          />
        )}
      </div>

      {/* Date/heure : simple paragraphe centré sous le titre */}
      {photo.timestamp && (
        <p className="pvw-date-center">
          {new Date(photo.timestamp).toLocaleString('fr-FR', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </p>
      )}

      {/* Toggle Pro */}
      {invitationStatus === 'accepted' && (
        <div className="pvw-switch-row">
          <label className="switch pvw-switch">
            <span className="label-text">Pro</span>
            <input
              type="checkbox"
              checked={Boolean(currentPhoto?.isPro)}
              onChange={() => handleToggleCategory(currentPhoto)}
            />
            <span className="slider"></span>
          </label>
        </div>
      )}

      {/* Image */}
      <div className="photo-content pvw-image-wrap">
        <img
          src={photo.url}
          alt={photo.name || 'Image affichée'}
          className="pvw-image"
          onLoad={(e) => {
            const { naturalWidth, naturalHeight } = e.currentTarget;
            setIsPortrait(naturalHeight > naturalWidth);
          }}
          style={{
            ...(isPortrait ? portraitStyle : landscapeStyle),
            ...baseImageStyle,
          }}
        />
      </div>

      {/* Bouton supprimer — toujours centré */}
      <div className="delete-container pvw-actions">
        <button
          className="delete-button pvw-btn danger"
          onClick={() => {
            if (window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
              handleDeletePhoto(photo.name, photo.category);
            }
          }}
          title="Supprimer la photo"
          type="button"
        >
          <FaTrash className="icon" /> <span>Supprimer</span>
        </button>
      </div>

      {/* Styles additionnels */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .pvw-root{
            min-height:100vh;
            min-height:100dvh;
            box-sizing:border-box;
            padding:16px 16px 48px;
            background:
              radial-gradient(900px 480px at 50% -12%, rgba(39,76,116,0.10), transparent 62%),
              var(--eb-bg);
            color:var(--eb-text);
            display:flex;
            flex-direction:column;
            align-items:center;
          }

          .pvw-bar{
            width:100%;
            max-width:960px;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:12px;
            margin:0 0 20px;
          }

          .pvw-title{
            width:100%;
            max-width:960px;
            display:flex;
            justify-content:center;
            align-items:center;
            margin:12px 0 6px;
            text-align:center;
          }

          .pvw-title-text{
            display:inline-block;
            max-width:100%;
            font-size:20px;
            font-weight:700;
            letter-spacing:0.02em;
            line-height:1.3;
            padding:6px 12px;
            border-radius:var(--eb-radius-sm);
            background:transparent;
            border:1px dashed transparent;
            box-shadow:none;
            cursor:text;
            user-select:none;
            overflow-wrap:anywhere;
            transition:border-color .15s ease, background-color .15s ease;
          }
          .pvw-title-text:hover{ border-color:var(--eb-border-strong); background:var(--eb-surface); }

          .pvw-title-input{
            width:min(560px, 100%);
            min-height:46px;
            padding:0 14px;
            border-radius:var(--eb-radius-sm);
            border:1px solid var(--eb-brand-500);
            box-shadow:var(--eb-ring);
            background:var(--eb-surface);
            font-size:17px;
            font-weight:600;
            text-align:center;
            text-transform:uppercase;
            cursor:text;
          }

          .pvw-date-center{
            width:100%;
            max-width:960px;
            text-align:center;
            margin:0 0 16px;
            color:var(--eb-muted);
            font-size:14px;
            font-weight:500;
          }

          .pvw-switch-row{
            width:100%;
            max-width:960px;
            display:flex;
            justify-content:center;
            margin:0 0 20px;
          }
          .pvw-switch{
            display:inline-flex;
            align-items:center;
            gap:10px;
            min-height:40px;
            padding:6px 14px;
            background:var(--eb-surface);
            border:1px solid var(--eb-border);
            border-radius:var(--eb-radius-pill);
            box-shadow:var(--eb-shadow-xs);
          }
          .pvw-switch .label-text{ font-weight:600; font-size:14px; color:var(--eb-text-2); }

          .pvw-image-wrap{
            width:100%;
            max-width:960px;
            display:flex;
            justify-content:center;
            align-items:center;
            padding:12px;
            background:var(--eb-surface);
            border:1px solid var(--eb-border);
            border-radius:var(--eb-radius-lg);
            box-shadow:var(--eb-shadow-sm);
            box-sizing:border-box;
          }
          .pvw-image{
            display:block;
            max-width:100% !important;
            border-radius:var(--eb-radius-sm);
            background:var(--eb-surface-2);
          }

          .pvw-actions{
            width:100%;
            max-width:960px;
            display:flex;
            justify-content:center;
            margin:20px 0 0;
          }

          .pvw-btn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            gap:8px;
            min-height:42px;
            margin:0;
            padding:0 16px;
            border-radius:var(--eb-radius-sm);
            border:1px solid transparent;
            font-size:14.5px;
            font-weight:600;
            line-height:1;
            cursor:pointer;
            user-select:none;
            box-shadow:var(--eb-shadow-xs);
            transition:background-color .15s ease, border-color .15s ease, color .15s ease;
          }
          .pvw-btn:focus-visible{ outline:none; box-shadow:var(--eb-ring); }
          .pvw-btn.icon-only{ width:42px; padding:0; }

          .pvw-btn.primary,
          .pvw-btn.ghost{
            background:var(--eb-surface);
            color:var(--eb-text-2);
            border-color:var(--eb-border-strong);
          }
          .pvw-btn.primary{ color:var(--eb-brand); }
          .pvw-btn.primary:hover,
          .pvw-btn.ghost:hover{ background:var(--eb-surface-2); color:var(--eb-text); }

          .pvw-btn.danger{
            background:var(--eb-surface);
            color:var(--eb-danger);
            border-color:#fbd0cb;
          }
          .pvw-btn.danger:hover{ background:var(--eb-danger-bg); border-color:var(--eb-danger); }

          @media (max-width:480px){
            .pvw-root{ padding:12px 12px 40px; }
            .pvw-title-text{ font-size:18px; }
            .pvw-image-wrap{ padding:8px; border-radius:var(--eb-radius); }
            .pvw-actions .pvw-btn{ width:100%; min-height:46px; }
          }

          @media (prefers-reduced-motion: reduce) {
            .pvw-btn, .pvw-title-text { transition:none; }
          }
        `,
        }}
      />
    </div>
  );
};

export default ClientPhotoViewer;
